import React, { useCallback, useEffect, useState } from "react";
import {
  CustomAutocomplete,
  CustomCard,
  CustomDialog,
  DropzoneComponent,
} from "../../../../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { Box, Divider, Typography } from "@material-ui/core";
import {
  CreatePropertyImage,
  UpdatePropertyImage,
  GetAllPropertyImages,
  lookupItemsGetId,
} from "../../../../../../../../Services";
import { PropertyImagesType } from "../../../../../../../../Enums";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";
import { getDownloadableLink } from "../../../../../../../../Helper";

// icons
import { DeleteIcon, Image01Icon } from "../../../../../../../../assets/icons";

// styles
import useStyle from "./styles";

function AddNewPropertyImageDialog({
  onOpen,
  onClose,
  propretyId,
  setRefetchDataPropertyImage,
}) {
  const styles = useStyle();
  const { setAlertBoxContent } = useVerticalNav();
  const { translate } = useTranslate("NewPropertiesView");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const [titleData, setTitleData] = useState([]);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [categoryHasImages, setCategoryHasImages] = useState(false);
  const [state, setState] = useState({
    propertyId: Number(propretyId) || null,
    imageType: PropertyImagesType.Image,
  });

  const [selected, setSelected] = useState({
    lookupItemName: "",
    lookupItemId: "",
  });

  const checkCategoryImages = async (categoryId, propertyId) => {
    if (categoryId && propertyId) {
      try {
        const res = await GetAllPropertyImages({
          categoryId,
          propertyId: Number(propertyId),
          pageIndex: 1,
          PageSize: 50,
        });

        if (res && Array.isArray(res.result) && res.result.length > 0) {
          setCategoryHasImages(true);
          setAllImages(res.result);
        } else {
          setCategoryHasImages(false);
          setAllImages([]);
        }
      } catch {
        setCategoryHasImages(false);
        setAllImages([]);
      }
    } else {
      setCategoryHasImages(false);
      setAllImages([]);
    }
  };

  const AddPropertiesImage = useCallback(async () => {
    try {
      const mappedFiles = allImages.map((file) => ({
        fileId: file?.uuid || file?.fileId || "",
        fileName: file?.fileName || file?.path || "",
      }));

      const body = {
        ...state,
        files: mappedFiles,
      };

      let res;
      if (categoryHasImages) {
        res = await UpdatePropertyImage(body);
      } else {
        res = await CreatePropertyImage(body);
      }
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate(
            categoryHasImages
              ? "update-Image-Property-successfully"
              : "create-Image-Property-successfully"
          ),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        setRefetchDataPropertyImage(true);
        onClose();
      } else {
        const errorMessage = res?.data?.Message || res?.message;
        if (
          errorMessage &&
          errorMessage.includes(
            "PROPERTY_IMAGE_ALREADY_EXISTS_PLEASE_UPDATE_THIS_PROPERTY_IMAGE"
          )
        ) {
          setAlertBoxContent({
            display: true,
            variant: "error",
            title: translate(
              "PROPERTY_IMAGE_ALREADY_EXISTS_PLEASE_UPDATE_THIS_PROPERTY_IMAGE_OR_SELECT_ANOTHER_PROPERTY"
            ),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        } else {
          setAlertBoxContent({
            display: true,
            variant: "error",
            title: translate("Something-went-wrong"),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        }
      }
    } catch (error) {
      console.error(error, "Something went wrong");
    }
  }, [state, allImages, categoryHasImages]);

  const getAllTitle = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: 1266,
    });
    if (!(res && res.status && res.status !== 200)) setTitleData(res || []);
    else setTitleData([]);
  }, []);

  const handleFilesAccepted = (files) => {
    setFilesAccepted(files);
  };

  const handleDeleteImage = (index) => {
    setAllImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileUploadMapping = () => {
    if (uploadedFileId && filesAccepted.length > 0) {
      const newFile = {
        fileId: uploadedFileId,
        fileSize: filesAccepted[0]?.size,
        fileName: filesAccepted[0]?.path,
        fileType: filesAccepted[0]?.type,
      };

      setAllImages((prev) => [...prev, newFile]);
    }
  };

  useEffect(() => {
    handleFileUploadMapping();
  }, [uploadedFileId]);

  useEffect(() => {
    getAllTitle();
  }, []);

  return (
    <CustomDialog
      open={onOpen}
      onClose={() => {
        onClose();
      }}
      title={translate("Add-new-property-images")}
      subtitle={translate(
        "Create-and-schedule-new-activity-to-enhance-engagement-with-your-contacts-and-leads"
      )}
      cancelText={translate("Cancel")}
      confirmText={translate("add")}
      onConfirm={() => {
        AddPropertiesImage();
      }}
      contentDialogClass={styles.containerContentDialog}
      width={"688px"}
      isDisabled={!state.categoryId || allImages.length === 0}
      content={
        <Box className={styles.wrapperDialog}>
          <Box className={styles.containerFiledItem}>
            <Box className={styles.containerTitles}>
              <Typography className={styles.titleItem}>
                {translate("Title")}
              </Typography>
              <Typography className={styles.subTitleItem}>
                {translate("Where-to-add-image")}
              </Typography>
            </Box>
            <CustomAutocomplete
              placeholder={translate("Select")}
              options={titleData}
              value={selected?.lookupItemName || null}
              onChange={async (e, value) => {
                setSelected((prev) => ({
                  ...prev,
                  lookupItemName: value,
                }));

                setState((prev) => ({
                  ...prev,
                  categoryId: value?.lookupItemId || null,
                }));

                await checkCategoryImages(value?.lookupItemId, propretyId);
              }}
              getOptionLabel={(option) => option?.lookupItemName || ""}
              getOptionValue={(option) => option?.lookupItemName || ""}
              renderOption={(option) => option?.lookupItemName || ""}
            />
          </Box>
          <Divider />
          <Box className={styles.containerFiledItem}>
            <Typography className={styles.titleItem}>
              {translate("Images")}
            </Typography>
            <Box className={styles.uploadedSection}>
              <DropzoneComponent
                accept="image/*"
                title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                onFilesAccepted={handleFilesAccepted}
                setUploadedFileId={setUploadedFileId}
              />
              {allImages && allImages.length > 0 && (
                <Box className={styles.sectionImage}>
                  <Box className={styles.containerImageTitle}>
                    <Typography className={styles.ImageTitle}>
                      {`${allImages.length} ${translate("Images")}`}
                    </Typography>
                    <Divider style={{ flexGrow: 1, alignSelf: "center" }} />
                  </Box>
                  <Box className={styles.containerUploadedFiles}>
                    {allImages.map((file, index) => (
                      <CustomCard
                        key={file.fileId || index}
                        boxShadow="xs"
                        borderRadius="xl"
                        borderColor="secondary"
                        classes={styles.cardUploadedFilesInfo}
                      >
                        <Box className={styles.containerImage}>
                          <img
                            src={getDownloadableLink(file.fileId)}
                            alt={file.fileName}
                            className={styles.wrapperImage}
                          />
                        </Box>

                        <Box
                          onClick={() => handleDeleteImage(index)}
                          className={styles.containerDeleteIcon}
                        >
                          <DeleteIcon
                            width="20"
                            height="20"
                            fill={palette.foreground.white}
                          />
                        </Box>

                        <Box className={styles.containerPlaceHolderImage}>
                          <Image01Icon
                            width="46"
                            height="46"
                            fill={palette.foreground.senary}
                          />
                        </Box>
                      </CustomCard>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      }
    />
  );
}

export default AddNewPropertyImageDialog;
